import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Users, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONI = [
  "Abruzzo", "Basilicata", "Calabria", "Campania", "Emilia-Romagna",
  "Friuli-Venezia Giulia", "Lazio", "Liguria", "Lombardia", "Marche",
  "Molise", "Piemonte", "Puglia", "Sardegna", "Sicilia", "Toscana",
  "Trentino-Alto Adige", "Umbria", "Valle d'Aosta", "Veneto"
];

export default function PublicDelegatiPage() {
  const [delegati, setDelegati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    regione: '',
    stato: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0
  });

  useEffect(() => {
    fetchDelegati();
  }, [filters.regione, filters.stato, pagination.page]);

  const fetchDelegati = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.regione) params.append('regione', filters.regione);
      if (filters.stato) params.append('stato', filters.stato);
      params.append('page', pagination.page);
      params.append('limit', 20);

      const response = await axios.get(`${API}/public/delegati?${params}`);
      setDelegati(response.data.items);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total
      });
    } catch (error) {
      console.error('Error fetching delegati:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(p => ({ ...p, page: 1 }));
    fetchDelegati();
  };

  const clearFilters = () => {
    setFilters({ search: '', regione: '', stato: '' });
    setPagination(p => ({ ...p, page: 1 }));
  };

  const countPresenti = delegati.filter(d => d.stato === 'presente').length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <img src="/logo-c3i.png" alt="C3i Logo" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Delegati C3i</h1>
              <p className="text-slate-500 text-sm">Elenco dei Delegati degli Ordini Territoriali</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card className="card-institutional" data-testid="public-delegati-card">
          <CardHeader>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-slate-600" />
                <div>
                  <CardTitle className="font-heading text-lg">Delegati C3i</CardTitle>
                  <p className="text-sm text-slate-500">{pagination.total} ordini totali</p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="mb-6 space-y-4">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Cerca per ordine o nominativo..."
                    value={filters.search}
                    onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                <Select 
                  value={filters.regione} 
                  onValueChange={(v) => {
                    setFilters(f => ({ ...f, regione: v === 'all' ? '' : v }));
                    setPagination(p => ({ ...p, page: 1 }));
                  }}
                >
                  <SelectTrigger className="w-full sm:w-48" data-testid="regione-filter">
                    <SelectValue placeholder="Tutte le regioni" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le regioni</SelectItem>
                    {REGIONI.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={filters.stato} 
                  onValueChange={(v) => {
                    setFilters(f => ({ ...f, stato: v === 'all' ? '' : v }));
                    setPagination(p => ({ ...p, page: 1 }));
                  }}
                >
                  <SelectTrigger className="w-full sm:w-40" data-testid="stato-filter">
                    <SelectValue placeholder="Tutti gli stati" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli stati</SelectItem>
                    <SelectItem value="presente">Presente</SelectItem>
                    <SelectItem value="assente">Assente</SelectItem>
                  </SelectContent>
                </Select>
                <Button type="submit" data-testid="search-btn">Cerca</Button>
              </form>
              {(filters.search || filters.regione || filters.stato) && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Rimuovi filtri
                </Button>
              )}
            </div>

            {/* Table */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner"></div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table" data-testid="delegati-table">
                    <thead>
                      <tr>
                        <th>Ordine</th>
                        <th>Regione</th>
                        <th>Delegato C3i</th>
                        <th>Supplente</th>
                        <th>Stato</th>
                      </tr>
                    </thead>
                    <tbody>
                      {delegati.map((d) => (
                        <tr key={d.ordine_id}>
                          <td>
                            <span className="font-medium">{d.ordine_denominazione}</span>
                            {d.ordine_sigla && (
                              <span className="text-slate-500 ml-1">({d.ordine_sigla})</span>
                            )}
                          </td>
                          <td>{d.regione}</td>
                          <td>
                            {d.delegato ? (
                              <span>{d.delegato.nome} {d.delegato.cognome}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td>
                            {d.supplente ? (
                              <span>{d.supplente.nome} {d.supplente.cognome}</span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td>
                            {d.stato === 'presente' ? (
                              <span className="badge-success">Presente</span>
                            ) : (
                              <span className="badge-warning">Assente</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                      data-testid="prev-page-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Precedente
                    </Button>
                    <span className="text-sm text-slate-600">
                      Pagina {pagination.page} di {pagination.pages} ({pagination.total} totali)
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                      data-testid="next-page-btn"
                    >
                      Successiva
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-sm text-slate-500">
            © 2026 C3i - Comitato Italiano Ingegneria dell'Informazione
          </p>
          <p className="text-center text-xs text-slate-400">
            Tutti i diritti riservati
          </p>
        </div>
      </div>
    </div>
  );
}
